import { ReactElement, useState, MouseEvent } from 'react'
import { Link as RouterLink, useParams } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import CloseIcon from '@mui/icons-material/Close'
import MenuIcon from '@mui/icons-material/Menu'
import RestoreIcon from '@mui/icons-material/Restore'
import {
  AppBar,
  Toolbar,
  Link,
  IconButton,
  Stack,
  Fade,
  Menu,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Switch,
  Tooltip,
  Theme,
  Typography,
  ListSubheader,
  Divider,
  Box,
} from '@mui/material'
import { useAuth0 } from '@auth0/auth0-react'
import logoIcon from '@img/logo-icon-green.png'
import { Form2, UnitConvertedField, TextField, FormIconButton } from '@ui/forms'
import { NestedMenuItem } from '@ui/navigation/NestedMenu'
import { useSnackbar } from '@ui/snackbar'
import { useAppStore, useUIStore } from '@stores'
import { appConfig } from 'src/constants'
import { appStoreInitialState } from 'src/state/stores/app'
import AIAccessWidget from './components/AIAccessWidget'
import AIChatWidget from './components/AIChatWidget'
import EditProject from './components/EditProject'
import LoadRenderConfigMenuItems from './components/LoadRenderConfigMenuItems'
import MenuToggleWithMatureness from './components/MaturenessIndicator'
import OrganisationSwitcher from './components/OrganisationSwitcher'
import ProjectInfo from './components/ProjectInfo'
import ProjectSettings from './components/ProjectSettings'
import RequestsWidget from './components/RequestsWidget'
import ResetPositionChecks from './components/ResetPositionChecks'
import { Logo } from './styles'

export const COMPUTE_CHECKS_WORKER_STRATEGIES: ComputeChecksWorkerStrategyType[] = [
  'task_group',
  'chunks_synchronous',
  'chunks_write_back',
  'fallback',
]

const Navbar = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const { logout, user } = useAuth0()
  const { projectId }: { projectId?: string } = useParams()

  const showSnow = useAppStore(state => state.showSnowV2)
  const setShowSnow = useAppStore(state => state.setShowSnow)

  const showStats = useAppStore(state => state.showStats)
  const setShowStats = useAppStore(state => state.setShowStats)

  const showNotifications = useAppStore(state => state.showNotifications)
  const setShowNotifications = useAppStore(state => state.setShowNotifications)

  const smartSuggestionsEnabled = useAppStore(state => state.smartSuggestionsEnabled)
  const setSmartSuggestionsEnabled = useAppStore(state => state.setSmartSuggestionsEnabled)

  const supportConfigToCheckRelPositionTolerance = useAppStore(
    state => state.supportConfigToCheckRelPositionTolerance,
  )
  const setSupportConfigToCheckRelPositionTolerance = useAppStore(
    state => state.setSupportConfigToCheckRelPositionTolerance,
  )

  const disableFormValidation = useAppStore(state => state.disableFormValidation)
  const setDisableFormValidation = useAppStore(state => state.setDisableFormValidation)

  const loadDisplayScale = useAppStore(state => state.loadDisplayScale)
  const setLoadDisplayScale = useAppStore(state => state.setLoadDisplayScale)

  const transparentOpacity = useAppStore(state => state.transparentOpacityV2)
  const setTransparentOpacity = useAppStore(state => state.setTransparentOpacity)
  const ifcTransparentOpacity = useAppStore(state => state.ifcTransparentOpacity)
  const setIfcTransparentOpacity = useAppStore(state => state.setIfcTransparentOpacity)

  const results2IntegratedAnchorView = useAppStore(state => state.results2IntegratedAnchorView)
  const setResults2IntegratedAnchorView = useAppStore(
    state => state.setResults2IntegratedAnchorView,
  )

  const dollySpeed = useAppStore(state => state.dollySpeed)
  const setDollySpeed = useAppStore(state => state.setDollySpeed)

  const floorplanPanStep = useAppStore(state => state.floorplanPanStep)
  const setFloorplanPanStep = useAppStore(state => state.setFloorplanPanStep)
  const floorplanZoomFactor = useAppStore(state => state.floorplanZoomFactor)
  const setFloorplanZoomFactor = useAppStore(state => state.setFloorplanZoomFactor)

  const clipPlaneSizeFromScene = useAppStore(state => state.clipPlaneSizeFromScene)
  const setClipPlaneSizeFromScene = useAppStore(state => state.setClipPlaneSizeFromScene)

  const sceneElementDebug = useAppStore(state => state.sceneElementDebug)
  const setSceneElementDebug = useAppStore(state => state.setSceneElementDebug)

  const batchedModelRenderingV2 = useAppStore(state => state.batchedModelRenderingV2)
  const disableOldPlans = useAppStore(state => state.disableOldPlans)
  const setDisableOldPlans = useAppStore(state => state.setDisableOldPlans)
  const setBatchedModelRenderingV2 = useAppStore(state => state.setBatchedModelRenderingV2)

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const open = !!anchorEl
  const setPortalRootLeft = useUIStore(state => state.setPortalRootLeft)
  const setPortalRootCenter = useUIStore(state => state.setPortalRootCenter)
  const setPortalRootRight = useUIStore(state => state.setPortalRootRight)
  const setPortalRootAiActions = useUIStore(state => state.setPortalRootAiActions)
  const reducedLogo = useUIStore(state => state.reducedLogo)

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget)
  }

  const handleClose = () => setAnchorEl(null)

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin + '/authorize' } })
  }

  const requestPermissions = async () => {
    try {
      const result = await Notification.requestPermission()
      if (result === 'denied' || result === 'default') {
        setShowNotifications(false)
        enqueueSnackbar('Keine Erlaubnis zum Senden von Benachrichtigungen erteilt', {
          variant: 'error',
        })
      }
    } catch (e) {
      setShowNotifications(false)
      enqueueSnackbar('Fehler bei der Anfrage zum Senden von Benachrichtigungen', {
        variant: 'error',
      })
    }
  }

  useEffectOnce(() => {
    if (disableFormValidation) {
      enqueueSnackbar({
        message: 'Validierung der Formulare deaktiviert. Bitte wieder deaktivieren.',
        variant: 'warning',
      })
    }
  })

  return (
    <AppBar
      elevation={0}
      sx={({ palette, distances }) => ({
        zIndex: (theme: Theme) => theme.zIndex.drawer + 1,
        backgroundColor: palette.common.white,
        color: palette.text.primary,
        borderTop: `2px solid ${palette.primary.main}`,
        borderBottom: `1px solid ${palette.divider}`,
        boxShadow: 'none',
        height: distances.navBarHeight,
        '@media (min-width: 1920px)': {
          height: distances.navBarHeightExpanded,
        },
      })}
    >
      <Toolbar
        variant="dense"
        disableGutters
        sx={({ distances }) => ({
          display: 'flex',
          gap: 0.5,
          minHeight: distances.navBarHeight,
          height: distances.navBarHeight,
          boxSizing: 'border-box',
          pt: 1,
          pb: 1,
          px: 1.5,
          '@media (min-width: 1920px)': {
            minHeight: distances.navBarHeightExpanded,
            height: distances.navBarHeightExpanded,
          },
        })}
      >
        <Link
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            mr: 0.5,
            ...(reducedLogo
              ? { width: 40, height: 40 }
              : { width: 'auto', height: 'auto', maxHeight: 32 }),
            '@media (min-width: 1920px)': reducedLogo
              ? { width: 44, height: 44 }
              : { maxHeight: 36 },
          }}
          component={RouterLink}
          to="/"
          title="Timberleicht Statik"
        >
          <Logo reduced={reducedLogo} src={logoIcon} />
        </Link>
        <Stack direction="row" flexGrow={1} justifyContent="space-between" alignItems="center">
          <Stack
            direction="row"
            alignItems="center"
            ref={ref => setPortalRootLeft(ref as HTMLDivElement)}
          />
          <Stack
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate3d(-50%, -50%, 0)',
              alignItems: 'center',
            }}
            direction="row"
            ref={ref => setPortalRootCenter(ref as HTMLDivElement)}
          />
          <Stack
            direction="row"
            alignItems="center"
            ref={ref => setPortalRootRight(ref as HTMLDivElement)}
          />
        </Stack>

        <Stack direction="row" alignItems="center" spacing={0.25}>
          {/* AI controls group: portal slot for editor-contributed AI actions
              (e.g. the instruction note button), kept beside the KI-Zugriff button */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.25}
            ref={ref => setPortalRootAiActions(ref as HTMLDivElement)}
          />
          <AIAccessWidget />
          <AIChatWidget />
          <RequestsWidget />

          <Tooltip title="Menü">
            <IconButton
              size="small"
              sx={{
                color: 'rgba(0, 0, 0, 0.54)',
                width: 32,
                height: 32,
                borderRadius: '50%',
                '@media (min-width: 1920px)': {
                  width: 36,
                  height: 36,
                },
              }}
              id="fade-button"
              aria-label="Menü"
              aria-controls={open ? 'fade-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={open ? 'true' : undefined}
              onClick={handleOpen}
              data-cy="navbar-menu"
            >
              <MenuIcon
                sx={{
                  fontSize: 16,
                  '@media (min-width: 1920px)': { fontSize: 18 },
                }}
              />
            </IconButton>
          </Tooltip>
          <Menu
            id="fade-menu"
            MenuListProps={{
              'aria-labelledby': 'fade-button',
            }}
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
            TransitionComponent={Fade}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                pr: 0.5,
                pt: 0.5,
              }}
            >
              <IconButton
                size="small"
                onClick={handleClose}
                aria-label="Menü schließen"
                data-cy="btn-navbar-menu-close"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
            <ListSubheader sx={{ lineHeight: 3 }}>Account</ListSubheader>

            {user && (
              <>
                <MenuItem>
                  <Tooltip
                    title={user.sub}
                    placement="left"
                    onClick={() => {
                      navigator.clipboard.writeText(user.sub || '')
                      enqueueSnackbar({
                        message: 'User ID in Zwischenablage kopiert',
                        variant: 'info',
                      })
                    }}
                  >
                    <Typography>{user.email}</Typography>
                  </Tooltip>
                </MenuItem>

                <MenuItem onClick={handleLogout}>Logout</MenuItem>
                <Divider />

                <OrganisationSwitcher parentMenuOpen={open} onSwitched={handleClose} />

                <Divider />
              </>
            )}

            {projectId && (
              <>
                <ListSubheader sx={{ lineHeight: 3 }}>Projekt</ListSubheader>

                <ProjectInfo />

                <EditProject onCloseNavbarMenu={handleClose} />

                <ProjectSettings parentMenuOpen={open} />

                <Divider sx={{ mt: 1, mb: 1 }} />
              </>
            )}

            <ListSubheader sx={{ lineHeight: 3 }}>App</ListSubheader>

            <>
              <NestedMenuItem
                label="Allgemein"
                parentMenuOpen={open}
                direction="left"
                leftIcon={<ChevronLeftIcon />}
              >
                <MenuToggleWithMatureness
                  checked={showNotifications}
                  setChecked={checked => {
                    if (checked) {
                      requestPermissions()
                    }
                    setShowNotifications(checked)
                  }}
                  tooltipTitle="Benachrichtigungen anzeigen - z.B. bei abgeschlossener Berechnung"
                  menuItemLabel="Benachrichtigungen"
                  matureness="enabled"
                />
                <MenuToggleWithMatureness
                  checked={smartSuggestionsEnabled}
                  setChecked={setSmartSuggestionsEnabled}
                  tooltipTitle="Smarte Vorschläge und Hinweise anzeigen"
                  menuItemLabel="Smarte Vorschläge"
                  matureness="enabled"
                />
                {appConfig.enableSnowEasterEgg && (
                  <MenuItem>
                    <FormGroup>
                      <Tooltip title="Schnee in der Scene anzeigen" placement="left">
                        <FormControlLabel
                          control={
                            <Switch
                              checked={showSnow}
                              onChange={e => setShowSnow(e.target.checked)}
                            />
                          }
                          label="Schnee"
                        />
                      </Tooltip>
                    </FormGroup>
                  </MenuItem>
                )}
              </NestedMenuItem>

              <NestedMenuItem
                label="Konfiguration"
                parentMenuOpen={open}
                direction="left"
                leftIcon={<ChevronLeftIcon />}
              >
                <MenuItem>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
                    <Tooltip
                      title={`Toleranz für die Zuordnung von Auflagerkonfigurationen zu Checks (default: ${appStoreInitialState.supportConfigToCheckRelPositionTolerance})`}
                      placement="left"
                    >
                      <Box>
                        <Form2
                          onSubmit={values => {
                            const opacity = parseFloat(
                              values.supportConfigToCheckRelPositionTolerance,
                            )
                            if (!isNaN(opacity) && opacity >= 0 && opacity <= 1) {
                              setSupportConfigToCheckRelPositionTolerance(opacity)
                            }
                          }}
                          defaultValues={{
                            supportConfigToCheckRelPositionTolerance:
                              supportConfigToCheckRelPositionTolerance.toString(),
                          }}
                          submitOnChange
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              inputLabel="Auflagerkonfiguration zu Check-Toleranz"
                              name="supportConfigToCheckRelPositionTolerance"
                              type="number"
                              size="small"
                              sx={{ flex: 1 }}
                              inputProps={{ min: 0, max: 0.1, disableDecimalScale: true }}
                            />
                            <Tooltip title="Auf Standardwert zurücksetzen" placement="top">
                              <Box>
                                <FormIconButton
                                  name="supportConfigToCheckRelPositionTolerance"
                                  value={
                                    appStoreInitialState.supportConfigToCheckRelPositionTolerance
                                  }
                                >
                                  <RestoreIcon fontSize="small" />
                                </FormIconButton>
                              </Box>
                            </Tooltip>
                          </Box>
                        </Form2>
                      </Box>
                    </Tooltip>
                  </Box>
                </MenuItem>

                <MenuItem>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
                    <Tooltip
                      title={`Transparenz-Opazität für IFC-Modelle (0.0 - 1.0) - (default: ${appStoreInitialState.ifcTransparentOpacity})`}
                      placement="left"
                    >
                      <Box>
                        <Form2
                          onSubmit={values => {
                            const opacity = parseFloat(values.ifcTransparentOpacity)
                            if (!isNaN(opacity) && opacity >= 0 && opacity <= 1) {
                              setIfcTransparentOpacity(opacity)
                            }
                          }}
                          defaultValues={{
                            ifcTransparentOpacity: ifcTransparentOpacity.toString(),
                          }}
                          submitOnChange
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              inputLabel="IFC Transparenz-Opazität"
                              name="ifcTransparentOpacity"
                              type="number"
                              size="small"
                              sx={{ flex: 1 }}
                              inputProps={{ min: 0, max: 1, step: 0.1 }}
                            />
                            <Tooltip title="Auf Standardwert zurücksetzen" placement="top">
                              <Box>
                                <FormIconButton
                                  name="ifcTransparentOpacity"
                                  value={appStoreInitialState.ifcTransparentOpacity}
                                >
                                  <RestoreIcon fontSize="small" />
                                </FormIconButton>
                              </Box>
                            </Tooltip>
                          </Box>
                        </Form2>
                      </Box>
                    </Tooltip>
                  </Box>
                </MenuItem>

                <MenuItem>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
                    <Tooltip
                      title={`Transparenz-Opazität für transparente Elemente (0.0 - 1.0) - (default: ${appStoreInitialState.transparentOpacityV2})`}
                      placement="left"
                    >
                      <Box>
                        <Form2
                          onSubmit={values => {
                            const opacity = parseFloat(values.transparentOpacity)
                            if (!isNaN(opacity) && opacity >= 0 && opacity <= 1) {
                              setTransparentOpacity(opacity)
                            }
                          }}
                          defaultValues={{ transparentOpacity: transparentOpacity.toString() }}
                          submitOnChange
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              inputLabel="Transparenz-Opazität"
                              name="transparentOpacity"
                              type="number"
                              size="small"
                              sx={{ flex: 1 }}
                              inputProps={{ min: 0, max: 1, step: 0.1 }}
                            />
                            <Tooltip title="Auf Standardwert zurücksetzen" placement="top">
                              <Box>
                                <FormIconButton
                                  name="transparentOpacity"
                                  value={appStoreInitialState.transparentOpacityV2}
                                >
                                  <RestoreIcon fontSize="small" />
                                </FormIconButton>
                              </Box>
                            </Tooltip>
                          </Box>
                        </Form2>
                      </Box>
                    </Tooltip>
                  </Box>
                </MenuItem>

                <MenuItem>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
                    <Tooltip
                      title={`Skalierung für die Darstellung von Lasten in der Scene (default: ${appStoreInitialState.loadDisplayScale})`}
                      placement="left"
                    >
                      <Box>
                        <Form2
                          onSubmit={values => {
                            setLoadDisplayScale(values.loadDisplayScale)
                          }}
                          defaultValues={{ loadDisplayScale }}
                          submitOnChange
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <UnitConvertedField
                              inputLabel="Maßstab für Lasten"
                              name="loadDisplayScale"
                              unitLabel="kN"
                              conversionFactor={1000}
                              size="small"
                              sx={{ flex: 1 }}
                              data-cy="load-display-scale-input"
                            />
                            <Tooltip title="Auf Standardwert zurücksetzen" placement="top">
                              <Box>
                                <FormIconButton
                                  name="loadDisplayScale"
                                  value={appStoreInitialState.loadDisplayScale}
                                >
                                  <RestoreIcon fontSize="small" />
                                </FormIconButton>
                              </Box>
                            </Tooltip>
                          </Box>
                        </Form2>
                      </Box>
                    </Tooltip>
                  </Box>
                </MenuItem>

                <LoadRenderConfigMenuItems />

                <MenuItem>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
                    <Tooltip
                      title={`Kamera-Zoom-Geschwindigkeit (Mausrad) (default: ${appStoreInitialState.dollySpeed})`}
                      placement="left"
                    >
                      <Box>
                        <Form2
                          onSubmit={values => {
                            const value = parseFloat(values.dollySpeed)
                            if (!isNaN(value) && value > 0 && value <= 5) {
                              setDollySpeed(value)
                            }
                          }}
                          defaultValues={{ dollySpeed: dollySpeed.toString() }}
                          submitOnChange
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              inputLabel="Kamera Dolly-Geschwindigkeit"
                              name="dollySpeed"
                              type="number"
                              size="small"
                              sx={{ flex: 1 }}
                              inputProps={{ min: 0.1, max: 5, step: 0.1 }}
                              data-cy="navbar-options-dolly-speed"
                            />
                            <Tooltip title="Auf Standardwert zurücksetzen" placement="top">
                              <Box>
                                <FormIconButton
                                  name="dollySpeed"
                                  value={appStoreInitialState.dollySpeed}
                                >
                                  <RestoreIcon fontSize="small" />
                                </FormIconButton>
                              </Box>
                            </Tooltip>
                          </Box>
                        </Form2>
                      </Box>
                    </Tooltip>
                  </Box>
                </MenuItem>

                <MenuItem>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
                    <Tooltip
                      title={`Strecke in Metern, um die die Grundrissansicht pro Pfeiltaste verschoben wird (default: ${appStoreInitialState.floorplanPanStep})`}
                      placement="left"
                    >
                      <Box>
                        <Form2
                          onSubmit={values => {
                            const value = parseFloat(values.floorplanPanStep)
                            if (!isNaN(value) && value > 0 && value <= 100) {
                              setFloorplanPanStep(value)
                            }
                          }}
                          defaultValues={{ floorplanPanStep: floorplanPanStep.toString() }}
                          submitOnChange
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              inputLabel="Grundriss-Schrittweite (m)"
                              name="floorplanPanStep"
                              type="number"
                              size="small"
                              sx={{ flex: 1 }}
                              inputProps={{ min: 0.01, max: 100, step: 0.1 }}
                              data-cy="navbar-options-floorplan-pan-step"
                            />
                            <Tooltip title="Auf Standardwert zurücksetzen" placement="top">
                              <Box>
                                <FormIconButton
                                  name="floorplanPanStep"
                                  value={appStoreInitialState.floorplanPanStep}
                                >
                                  <RestoreIcon fontSize="small" />
                                </FormIconButton>
                              </Box>
                            </Tooltip>
                          </Box>
                        </Form2>
                      </Box>
                    </Tooltip>
                  </Box>
                </MenuItem>

                <MenuItem>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 200 }}>
                    <Tooltip
                      title={`Faktor, um den die Grundrissansicht pro Plus- oder Minus-Taste gezoomt wird (default: ${appStoreInitialState.floorplanZoomFactor})`}
                      placement="left"
                    >
                      <Box>
                        <Form2
                          onSubmit={values => {
                            const value = parseFloat(values.floorplanZoomFactor)
                            if (!isNaN(value) && value > 1 && value <= 5) {
                              setFloorplanZoomFactor(value)
                            }
                          }}
                          defaultValues={{ floorplanZoomFactor: floorplanZoomFactor.toString() }}
                          submitOnChange
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              inputLabel="Grundriss-Zoomfaktor"
                              name="floorplanZoomFactor"
                              type="number"
                              size="small"
                              sx={{ flex: 1 }}
                              inputProps={{ min: 1.05, max: 5, step: 0.05 }}
                              data-cy="navbar-options-floorplan-zoom-factor"
                            />
                            <Tooltip title="Auf Standardwert zurücksetzen" placement="top">
                              <Box>
                                <FormIconButton
                                  name="floorplanZoomFactor"
                                  value={appStoreInitialState.floorplanZoomFactor}
                                >
                                  <RestoreIcon fontSize="small" />
                                </FormIconButton>
                              </Box>
                            </Tooltip>
                          </Box>
                        </Form2>
                      </Box>
                    </Tooltip>
                  </Box>
                </MenuItem>

                <MenuItem data-cy="btn-clip-plane-size-from-scene">
                  <FormGroup>
                    <Tooltip
                      title="Neue Schnittflächen an die sichtbare Geometrie in der Scene anpassen (sonst feste 40×40 m)"
                      placement="left"
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={clipPlaneSizeFromScene}
                            onChange={e => setClipPlaneSizeFromScene(e.target.checked)}
                          />
                        }
                        label="Schnittfläche an Scene anpassen"
                      />
                    </Tooltip>
                  </FormGroup>
                </MenuItem>
              </NestedMenuItem>

              <NestedMenuItem
                label="Features"
                parentMenuOpen={open}
                direction="left"
                leftIcon={<ChevronLeftIcon />}
              >
                <NestedMenuItem
                  label="Performance"
                  parentMenuOpen={open}
                  direction="left"
                  leftIcon={<ChevronLeftIcon />}
                >
                  <MenuToggleWithMatureness
                    checked={batchedModelRenderingV2}
                    setChecked={setBatchedModelRenderingV2}
                    tooltipTitle="Wände, Decken, Dächer und vertikale Decken gebündelt als flache Flächen rendern (weniger Draw-Calls). Elemente verlieren ihre Dicke."
                    menuItemLabel="Model Rendering Performance Batched"
                    matureness="experimental"
                    dataCy="navbar-options-batched-model-rendering-v2"
                  />
                </NestedMenuItem>

                <NestedMenuItem
                  label="Ergebnisse"
                  parentMenuOpen={open}
                  direction="left"
                  leftIcon={<ChevronLeftIcon />}
                >
                  <MenuToggleWithMatureness
                    checked={results2IntegratedAnchorView}
                    setChecked={setResults2IntegratedAnchorView}
                    tooltipTitle="Zeigt die Zuganker als eigenen Tab „Anker“ in der Ergebnisansicht statt als Zugverbindertabelle"
                    menuItemLabel="Zuganker in der Ergebnisansicht"
                    matureness="experimental"
                    dataCy="navbar-options-results2-integrated-anchor-view"
                  />
                </NestedMenuItem>

                <NestedMenuItem
                  label="Pläne"
                  parentMenuOpen={open}
                  direction="left"
                  leftIcon={<ChevronLeftIcon />}
                >
                  <MenuToggleWithMatureness
                    checked={disableOldPlans}
                    setChecked={setDisableOldPlans}
                    tooltipTitle="Blendet die alte Seite „Pläne“ in der linken Leiste aus – Pläne werden in der „Planansicht“ erzeugt und verwaltet"
                    menuItemLabel="Alte Pläne-Seite ausblenden"
                    matureness="mature"
                    dataCy="navbar-options-disable-old-plans"
                  />
                </NestedMenuItem>
              </NestedMenuItem>

              <NestedMenuItem
                label="Dev Tools"
                parentMenuOpen={open}
                direction="left"
                leftIcon={<ChevronLeftIcon />}
                data-cy="dev-tools-menu"
              >
                <MenuItem>
                  <FormGroup>
                    <Tooltip title="Scene Stats anzeigen (debugging)" placement="left">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={showStats}
                            onChange={e => setShowStats(e.target.checked)}
                          />
                        }
                        label="Scene Stats"
                      />
                    </Tooltip>
                  </FormGroup>
                </MenuItem>
                <MenuItem>
                  <FormGroup>
                    <Tooltip
                      title="Klick-Inspektor: zeigt alle Elemente unter dem Cursor (oberstes zuerst)"
                      placement="left"
                    >
                      <FormControlLabel
                        control={
                          <Switch
                            checked={sceneElementDebug}
                            onChange={e => setSceneElementDebug(e.target.checked)}
                          />
                        }
                        label="Scene Element Debug"
                      />
                    </Tooltip>
                  </FormGroup>
                </MenuItem>
                <MenuToggleWithMatureness
                  checked={disableFormValidation}
                  setChecked={checked => {
                    enqueueSnackbar({
                      message: 'Seite neu laden um die Änderung anzuwenden',
                      variant: 'warning',
                    })
                    setDisableFormValidation(checked)
                  }}
                  tooltipTitle="Validierung von Formularen deaktivieren - nur im Notfall nutzen"
                  menuItemLabel="Validierung von Formularen deaktivieren"
                  matureness="discouraged"
                />
                <ResetPositionChecks />
              </NestedMenuItem>
            </>
          </Menu>
        </Stack>
      </Toolbar>
    </AppBar>
  )
}

export default Navbar
